// The small confirm dialogs (acceptance H, P, Q): Stop, Remove (blocked while
// anything uses the model), and the generic Keep / <action> confirm.
import { useEffect, useState } from "react";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { removeBlockText } from "./modelServingModel";
import { Btn, Dialog, Notice, errorText } from "./modelServingUi";

/** Runs ``action`` once; shows its error inline instead of closing. */
function useAction(action, onDone, onClose) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async () => {
    setBusy(true); setErr(null);
    try {
      await action();
      onDone?.();
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  return { busy, err, run };
}

export function StopDialog({ model, onClose, onDone }) {
  const { busy, err, run } = useAction(() => modelServingApi.stop(model.key), onDone, onClose);
  return (
    <Dialog testid="stop-dialog" title={`Stop ${model.display_name}?`} glyph="⏹" maxWidth={440} onClose={onClose}
      sub="Parks the model at zero and releases the GPU. It stays in your rankings and warms again (from S3) on the next request."
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Keep running</Btn>
          <Btn kind="danger" data-lc="stop-confirm" disabled={busy} onClick={run}>Stop &amp; park</Btn>
        </>
      )}>
      {err && <Notice testid="stop-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}

export function RemoveDialog({ model, onClose, onDone }) {
  const [usage, setUsage] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const { busy, err, run } = useAction(() => modelServingApi.remove(model.key), onDone, onClose);

  useEffect(() => {
    let live = true;
    modelServingApi.usage(model.key)
      .then((r) => { if (live) setUsage(r.data || {}); })
      .catch((e) => { if (live) setLoadErr(errorText(e)); });
    return () => { live = false; };
  }, [model.key]);

  const blocked = usage ? removeBlockText(usage) : "";
  return (
    <Dialog testid="remove-dialog" title={`Remove ${model.display_name}?`} glyph="🗑" maxWidth={480} onClose={onClose}
      sub="Stops the model, removes its endpoint and deletes its weights from your S3 library."
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="danger" disabled={busy || !usage || !!blocked} onClick={run}>Remove model</Btn>
        </>
      )}>
      {!usage && !loadErr && <div style={{ fontSize: 12.5, color: COLORS.textDim }}>Checking what uses this model…</div>}
      {loadErr && <Notice tone="bad" icon="!">{`Couldn't check what uses this model: ${loadErr}`}</Notice>}
      {blocked && (
        <Notice lc="remove-blocked" tone="bad" icon="⛔">
          <b style={{ color: COLORS.text }}>Still in use.</b> {blocked} Remove it from those first, then try again.
        </Notice>
      )}
      {usage && !blocked && (
        <div data-lc="remove-scope" style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 12 }}>
          Deletes <b style={{ color: COLORS.text }}>all versions of this model</b> from S3. You can add it again from
          Hugging Face later.
        </div>
      )}
      {err && <Notice testid="remove-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}

/** Q1 — a specific title, what gets deleted, "Keep" and a named action. */
export function ConfirmDialog({ title, body, actionLabel, action, onClose, onDone }) {
  const { busy, err, run } = useAction(action, onDone, onClose);
  return (
    <Dialog testid="confirm-dialog" title={title} glyph="!" maxWidth={440} onClose={onClose} sub={body}
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Keep</Btn>
          <Btn kind="danger" disabled={busy} onClick={run}>{actionLabel}</Btn>
        </>
      )}>
      {err && <Notice testid="confirm-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}
